<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div class="card report-card">
          <div class="card-body">
            <div class="report-header-bar">
              <div>
                <h5>GST / Tax Report</h5>
                <p>GST collected on sales, GST credit on purchases and what you owe, plus GSTR-1, HSN and item-wise views for filing. Returns are included once confirmed.</p>
              </div>
              <div class="export-actions">
                <button
                  class="btn btn-outline-success"
                  type="button"
                  @click="downloadReport('excel')"
                  :disabled="exportLoading"
                >
                  <vue-feather type="download" size="15"></vue-feather>
                  {{ exportLoading && exportFormat === "excel" ? "Preparing..." : "Excel" }}
                </button>
                <button
                  class="btn btn-outline-danger"
                  type="button"
                  @click="downloadReport('pdf')"
                  :disabled="exportLoading"
                >
                  <vue-feather type="file-text" size="15"></vue-feather>
                  {{ exportLoading && exportFormat === "pdf" ? "Preparing..." : "PDF" }}
                </button>
              </div>
            </div>

            <div class="report-toolbar">
              <div class="row g-3 align-items-end">
                <div class="col-12 col-md-6 col-lg-3">
                  <label>From Date</label>
                  <b-form-input v-model="filters.startdate" type="date" />
                </div>
                <div class="col-12 col-md-6 col-lg-3">
                  <label>To Date</label>
                  <b-form-input v-model="filters.enddate" type="date" />
                </div>
                <div class="col-12 col-md-6 col-lg-3">
                  <label>Report Type</label>
                  <b-form-select v-model="filters.report_type" :options="reportTypeOptions" />
                </div>
                <div class="col-12 col-md-6 col-lg-3">
                  <div class="d-flex gap-2 report-actions">
                    <button class="btn btn-submit report-action-btn" type="button" :disabled="loading" @click="fetchReport">
                      {{ loading ? "Loading..." : "View Report" }}
                    </button>
                    <button class="btn btn-outline-secondary report-action-btn" type="button" @click="resetFilters">
                      Reset
                    </button>
                  </div>
                </div>
              </div>
              <ReportDatePresets :from="filters.startdate" :to="filters.enddate" @select="applyPeriod" />
            </div>

            <div class="summary-grid">
              <div class="summary-card outward">
                <span>Output GST on Sales</span>
                <strong>₹{{ formatCurrency(summary.outward_tax) }}</strong>
                <small>Taxable value: ₹{{ formatCurrency(summary.outward_taxable) }}</small>
              </div>
              <template v-if="showsInputTax">
                <div class="summary-card input">
                  <span>Input GST Credit</span>
                  <strong>₹{{ formatCurrency(summary.input_tax) }}</strong>
                  <small>Taxable value: ₹{{ formatCurrency(summary.input_taxable) }}</small>
                </div>
                <div :class="['summary-card', 'payable', netTaxPayable > 0 ? 'tone-negative' : 'tone-positive']">
                  <span>{{ netTaxPayable >= 0 ? "Net GST Payable" : "GST Credit Carried Forward" }}</span>
                  <strong>₹{{ formatCurrency(Math.abs(netTaxPayable)) }}</strong>
                  <small>Output GST less input GST credit</small>
                </div>
              </template>
              <template v-else>
                <div class="summary-card">
                  <span>CGST + SGST</span>
                  <strong>₹{{ formatCurrency(Number(summary.outward_cgst || 0) + Number(summary.outward_sgst || 0)) }}</strong>
                  <small>Intra-state sales</small>
                </div>
                <div class="summary-card">
                  <span>IGST</span>
                  <strong>₹{{ formatCurrency(summary.outward_igst) }}</strong>
                  <small>Inter-state sales</small>
                </div>
              </template>
            </div>

            <div v-if="showsInputTax" class="breakdown-grid">
              <div class="breakdown-card">
                <h6>Output Tax</h6>
                <div><span>CGST</span><strong>{{ formatCurrency(summary.outward_cgst) }}</strong></div>
                <div><span>SGST</span><strong>{{ formatCurrency(summary.outward_sgst) }}</strong></div>
                <div><span>IGST</span><strong>{{ formatCurrency(summary.outward_igst) }}</strong></div>
              </div>
              <div class="breakdown-card">
                <h6>Input Credit</h6>
                <div><span>CGST</span><strong>{{ formatCurrency(summary.input_cgst) }}</strong></div>
                <div><span>SGST</span><strong>{{ formatCurrency(summary.input_sgst) }}</strong></div>
                <div><span>IGST</span><strong>{{ formatCurrency(summary.input_igst) }}</strong></div>
              </div>
            </div>

            <h6 class="table-title">{{ tableTitle }}</h6>
            <div v-if="isItemwiseReport" class="table-responsive itemwise-report-wrap">
              <table class="gst-itemwise-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Item Code</th>
                    <th>Item</th>
                    <th class="numeric">Qty</th>
                    <th class="numeric">Sale Price</th>
                    <th class="numeric">Sales Value</th>
                    <th class="numeric" colspan="2">GST % and Value</th>
                    <th class="numeric">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!loading && displayRows.length === 0">
                    <td colspan="10" class="empty-row">No GST itemwise data found.</td>
                  </tr>
                  <tr
                    v-for="record in displayRows"
                    :key="rowKey(record)"
                    :class="rowClassName(record)"
                  >
                    <template v-if="record.rowtype === 'gst_header'">
                      <td colspan="10" class="slab-title">{{ formatRate(record.taxpercent) }}</td>
                    </template>
                    <template v-else-if="record.rowtype === 'gst_total'">
                      <td colspan="4"></td>
                      <td class="numeric money-text">{{ formatNumber(record.quantity) }}</td>
                      <td class="total-label">Total</td>
                      <td class="numeric money-text">{{ formatNumber(record.sales_value) }}</td>
                      <td></td>
                      <td class="numeric money-text">{{ formatNumber(record.gst_value) }}</td>
                      <td class="numeric money-text">{{ formatNumber(record.total) }}</td>
                    </template>
                    <template v-else>
                      <td>{{ record.serialno }}</td>
                      <td>{{ formatDate(record.reportdate) }}</td>
                      <td>{{ record.itemcode || "-" }}</td>
                      <td>{{ record.itemname || "-" }}</td>
                      <td class="numeric money-text">{{ formatNumber(record.quantity) }}</td>
                      <td class="numeric money-text">{{ formatNumber(record.saleprice) }}</td>
                      <td class="numeric money-text">{{ formatNumber(record.sales_value) }}</td>
                      <td class="numeric money-text">{{ formatPlainRate(record.taxpercent) }}</td>
                      <td class="numeric money-text">{{ formatNumber(record.gst_value) }}</td>
                      <td class="numeric money-text">{{ formatNumber(record.total) }}</td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="table-responsive">
              <a-table
                :columns="columns"
                :data-source="rows"
                :loading="loading"
                :pagination="tablePagination"
                :row-key="rowKey"
                :row-class-name="rowClassName"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'reportdate'">
                    {{ formatDate(record.reportdate) }}
                  </template>
                  <template v-else-if="column.key === 'serialno'">
                    {{ record.serialno || "" }}
                  </template>
                  <template v-else-if="column.key === 'taxpercent'">
                    {{ isHsnReport ? formatPlainRate(record.taxpercent) : formatRate(record.taxpercent) }}
                  </template>
                  <template v-else-if="column.key === 'hsncode'">
                    {{ record.hsncode || "" }}
                  </template>
                  <template v-else-if="column.key === 'hsn_quantity'">
                    <span class="money-text">{{ formatHsnQuantity(record) }}</span>
                  </template>
                  <template v-else-if="['taxtype', 'itemcode', 'itemname', 'billno', 'virtual_invoice_number', 'original_invoice_number', 'customername', 'gstin'].includes(column.key)">
                    {{ record[column.key] || "-" }}
                  </template>
                  <template v-else-if="['quantity', 'saleprice', 'sales_value', 'gst_value', 'total', 'taxable_0', 'taxable_5', 'taxable_12', 'taxable_18', 'taxable_28'].includes(column.key)">
                    <span class="money-text">{{ formatNumber(record[column.key]) }}</span>
                  </template>
                  <template v-else>
                    <span class="money-text">{{ formatCurrency(record[column.key]) }}</span>
                  </template>
                </template>
              </a-table>
            </div>

            <div v-if="isGstr1Report" class="gstr1-end-summary">
              <div class="gstr1-total-strip">
                <span>TOTAL</span>
                <strong>{{ formatNumber(gstr1Totals.taxable_0) }}</strong>
                <strong>{{ formatNumber(gstr1Totals.taxable_5) }}</strong>
                <strong>{{ formatNumber(gstr1Totals.taxable_12) }}</strong>
                <strong>{{ formatNumber(gstr1Totals.taxable_18) }}</strong>
                <strong>{{ formatNumber(gstr1Totals.taxable_28) }}</strong>
                <strong>{{ formatNumber(gstr1Totals.sgst) }}</strong>
                <strong>{{ formatNumber(gstr1Totals.cgst) }}</strong>
                <strong>{{ formatNumber(gstr1Totals.igst) }}</strong>
                <strong>{{ formatNumber(gstr1Totals.total) }}</strong>
              </div>

              <div class="gstr1-tax-summary-grid">
                <table class="gstr1-tax-table">
                  <thead>
                    <tr>
                      <th colspan="5">SGST AND CGST Details</th>
                    </tr>
                    <tr>
                      <th>GST %</th>
                      <th class="numeric">Taxable</th>
                      <th class="numeric">SGST</th>
                      <th class="numeric">CGST</th>
                      <th class="numeric">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in gstr1SgstCgstRows" :key="`cgst-${row.rate}`">
                      <td>{{ row.label }}</td>
                      <td class="numeric">{{ formatNumber(row.taxable) }}</td>
                      <td class="numeric">{{ formatNumber(row.sgst) }}</td>
                      <td class="numeric">{{ formatNumber(row.cgst) }}</td>
                      <td class="numeric">{{ formatNumber(row.total) }}</td>
                    </tr>
                    <tr class="summary-total-row">
                      <td>TOTAL</td>
                      <td class="numeric">{{ formatNumber(gstr1SgstCgstTotals.taxable) }}</td>
                      <td class="numeric">{{ formatNumber(gstr1SgstCgstTotals.sgst) }}</td>
                      <td class="numeric">{{ formatNumber(gstr1SgstCgstTotals.cgst) }}</td>
                      <td class="numeric">{{ formatNumber(gstr1SgstCgstTotals.total) }}</td>
                    </tr>
                  </tbody>
                </table>

                <table class="gstr1-tax-table">
                  <thead>
                    <tr>
                      <th colspan="4">IGST Details</th>
                    </tr>
                    <tr>
                      <th>GST %</th>
                      <th class="numeric">Taxable</th>
                      <th class="numeric">IGST</th>
                      <th class="numeric">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in gstr1IgstRows" :key="`igst-${row.rate}`">
                      <td>{{ row.label }}</td>
                      <td class="numeric">{{ formatNumber(row.taxable) }}</td>
                      <td class="numeric">{{ formatNumber(row.igst) }}</td>
                      <td class="numeric">{{ formatNumber(row.total) }}</td>
                    </tr>
                    <tr class="summary-total-row">
                      <td>TOTAL</td>
                      <td class="numeric">{{ formatNumber(gstr1IgstTotals.taxable) }}</td>
                      <td class="numeric">{{ formatNumber(gstr1IgstTotals.igst) }}</td>
                      <td class="numeric">{{ formatNumber(gstr1IgstTotals.total) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import ReportDatePresets from "@/components/report/ReportDatePresets.vue";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

const emptySummary = () => ({
  outward_taxable: 0,
  outward_cgst: 0,
  outward_sgst: 0,
  outward_igst: 0,
  outward_tax: 0,
  input_taxable: 0,
  input_cgst: 0,
  input_sgst: 0,
  input_igst: 0,
  input_tax: 0,
  net_tax_payable: 0,
});

const taxColumns = [
  { title: "Tax Type", dataIndex: "taxtype", key: "taxtype", width: 98 },
  { title: "GST %", dataIndex: "taxpercent", key: "taxpercent", width: 86 },
  { title: "Sales Taxable", dataIndex: "outward_taxable", key: "outward_taxable", align: "right" },
  { title: "Output CGST", dataIndex: "outward_cgst", key: "outward_cgst", align: "right" },
  { title: "Output SGST", dataIndex: "outward_sgst", key: "outward_sgst", align: "right" },
  { title: "Output IGST", dataIndex: "outward_igst", key: "outward_igst", align: "right" },
  { title: "Output GST", dataIndex: "outward_tax", key: "outward_tax", align: "right" },
  { title: "Purchase Taxable", dataIndex: "input_taxable", key: "input_taxable", align: "right" },
  { title: "Input GST", dataIndex: "input_tax", key: "input_tax", align: "right" },
  { title: "Net Tax", dataIndex: "net_tax", key: "net_tax", align: "right" },
];

const itemwiseColumns = [
  { title: "Sl.No.", dataIndex: "serialno", key: "serialno", width: 70 },
  { title: "Date", dataIndex: "reportdate", key: "reportdate", width: 110 },
  { title: "Item Code", dataIndex: "itemcode", key: "itemcode", width: 120 },
  { title: "Item Desc", dataIndex: "itemname", key: "itemname", width: 220 },
  { title: "Qty", dataIndex: "quantity", key: "quantity", align: "right" },
  { title: "Sale Price", dataIndex: "saleprice", key: "saleprice", align: "right" },
  { title: "Sales Value", dataIndex: "sales_value", key: "sales_value", align: "right" },
  { title: "GST %", dataIndex: "taxpercent", key: "taxpercent", align: "right" },
  { title: "GST Value", dataIndex: "gst_value", key: "gst_value", align: "right" },
  { title: "Total", dataIndex: "total", key: "total", align: "right" },
];

const gstr1Columns = [
  { title: "Sl.No.", dataIndex: "serialno", key: "serialno", width: 70 },
  { title: "Invoice No", dataIndex: "billno", key: "billno", width: 120 },
  { title: "Invoice Date", dataIndex: "reportdate", key: "reportdate", width: 120 },
  { title: "Particulars", dataIndex: "customername", key: "customername", width: 200 },
  { title: "GSTIN", dataIndex: "gstin", key: "gstin", width: 150 },
  { title: "Taxable 0%", dataIndex: "taxable_0", key: "taxable_0", align: "right" },
  { title: "Taxable 5%", dataIndex: "taxable_5", key: "taxable_5", align: "right" },
  { title: "Taxable 12%", dataIndex: "taxable_12", key: "taxable_12", align: "right" },
  { title: "Taxable 18%", dataIndex: "taxable_18", key: "taxable_18", align: "right" },
  { title: "Taxable 28%", dataIndex: "taxable_28", key: "taxable_28", align: "right" },
  { title: "SGST", dataIndex: "outward_sgst", key: "outward_sgst", align: "right" },
  { title: "CGST", dataIndex: "outward_cgst", key: "outward_cgst", align: "right" },
  { title: "IGST", dataIndex: "outward_igst", key: "outward_igst", align: "right" },
  { title: "Total", dataIndex: "total", key: "total", align: "right" },
];

const hsnColumns = [
  { title: "HSN Code", dataIndex: "hsncode", key: "hsncode", width: 120 },
  { title: "Name", dataIndex: "itemname", key: "itemname", width: 280 },
  { title: "Tax %", dataIndex: "taxpercent", key: "taxpercent", align: "right" },
  { title: "Qty", dataIndex: "quantity", key: "hsn_quantity", align: "right" },
  { title: "Taxable", dataIndex: "outward_taxable", key: "outward_taxable", align: "right" },
  { title: "SGST", dataIndex: "outward_sgst", key: "outward_sgst", align: "right" },
  { title: "CGST", dataIndex: "outward_cgst", key: "outward_cgst", align: "right" },
  { title: "IGST", dataIndex: "outward_igst", key: "outward_igst", align: "right" },
  { title: "Total", dataIndex: "total", key: "total", align: "right" },
];

const systemInternalColumns = [
  { title: "Sl.No.", dataIndex: "serialno", key: "serialno", width: 70 },
  { title: "Virtual Invoice No", dataIndex: "virtual_invoice_number", key: "virtual_invoice_number", width: 170 },
  { title: "Original Invoice No", dataIndex: "original_invoice_number", key: "original_invoice_number", width: 170 },
  { title: "Invoice Date", dataIndex: "reportdate", key: "reportdate", width: 120 },
  { title: "Customer", dataIndex: "customername", key: "customername", width: 200 },
  { title: "GSTIN", dataIndex: "gstin", key: "gstin", width: 150 },
  { title: "Taxable Amount", dataIndex: "outward_taxable", key: "outward_taxable", align: "right" },
  { title: "CGST", dataIndex: "outward_cgst", key: "outward_cgst", align: "right" },
  { title: "SGST", dataIndex: "outward_sgst", key: "outward_sgst", align: "right" },
  { title: "IGST", dataIndex: "outward_igst", key: "outward_igst", align: "right" },
  { title: "Total Tax", dataIndex: "outward_tax", key: "outward_tax", align: "right" },
  { title: "Net Amount", dataIndex: "total", key: "total", align: "right" },
];

export default {
  name: "gstreport",
  components: { ReportDatePresets },
  data() {
    return {
      title: "GST / Tax Report",
      title1: "GST liability and input credit by tax rate",
      loading: false,
      exportLoading: false,
      exportFormat: "",
      rows: [],
      summary: emptySummary(),
      filters: {
        startdate: this.defaultStartDate(),
        enddate: this.today(),
        report_type: "summary",
      },
    };
  },
  computed: {
    reportTypeOptions() {
      const options = [
        { value: "summary", text: "Summary Report" },
        { value: "gstr1", text: "GSTR-1" },
        { value: "hsn", text: "HSN Report" },
        { value: "itemwise", text: "Item Wise Report" },
      ];
      if (this.isSystemInternalUser) {
        options.unshift({ value: "system_internal", text: "System Internal GST Report" });
      }
      return options;
    },
    isSystemInternalUser() {
      return String(this.$store?.getters?.getUserRole || "").toLowerCase() === "system_internal";
    },
    tableTitle() {
      if (this.filters.report_type === "gstr1") return "GSTR-1 Report";
      if (this.filters.report_type === "hsn") return "HSN Report";
      if (this.filters.report_type === "itemwise") return "GST Item Wise Report";
      if (this.filters.report_type === "system_internal") return "System Internal GST Report";
      return "GST Rate Summary";
    },
    showsInputTax() {
      // Only the rate summary includes purchases; the other GST views cover sales only.
      return this.filters.report_type === "summary";
    },
    netTaxPayable() {
      return Number(this.summary.net_tax_payable) || 0;
    },
    isItemwiseReport() {
      return this.filters.report_type === "itemwise";
    },
    isGstr1Report() {
      return this.filters.report_type === "gstr1";
    },
    isHsnReport() {
      return this.filters.report_type === "hsn";
    },
    tablePagination() {
      if (!this.isGstr1Report || this.rows.length <= 25) return false;
      return {
        pageSize: 25,
        showSizeChanger: true,
        pageSizeOptions: ["25", "50", "100"],
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
      };
    },
    gstr1RateRows() {
      const rates = [
        { rate: 0, field: "taxable_0", label: "0%" },
        { rate: 5, field: "taxable_5", label: "5%" },
        { rate: 12, field: "taxable_12", label: "12%" },
        { rate: 18, field: "taxable_18", label: "18%" },
        { rate: 28, field: "taxable_28", label: "28%" },
      ];

      return rates.map((rateInfo) => {
        const cgstSgst = { taxable: 0, sgst: 0, cgst: 0, total: 0 };
        const igst = { taxable: 0, igst: 0, total: 0 };

        this.rows.forEach((row) => {
          const taxable = Number(row[rateInfo.field] || 0);
          if (!taxable) return;

          const isIgst = Number(row.outward_igst || 0) !== 0;
          const tax = taxable * rateInfo.rate / 100;
          if (isIgst) {
            igst.taxable += taxable;
            igst.igst += tax;
            igst.total += taxable + tax;
            return;
          }

          cgstSgst.taxable += taxable;
          cgstSgst.sgst += tax / 2;
          cgstSgst.cgst += tax / 2;
          cgstSgst.total += taxable + tax;
        });

        return {
          rate: rateInfo.rate,
          label: rateInfo.label,
          cgstSgst: this.roundSummaryRow(cgstSgst),
          igst: this.roundSummaryRow(igst),
        };
      });
    },
    gstr1SgstCgstRows() {
      return this.gstr1RateRows.map((row) => ({ rate: row.rate, label: row.label, ...row.cgstSgst }));
    },
    gstr1IgstRows() {
      return this.gstr1RateRows.map((row) => ({ rate: row.rate, label: row.label, ...row.igst }));
    },
    gstr1SgstCgstTotals() {
      return this.totalGstr1SummaryRows(this.gstr1SgstCgstRows, ["taxable", "sgst", "cgst", "total"]);
    },
    gstr1IgstTotals() {
      return this.totalGstr1SummaryRows(this.gstr1IgstRows, ["taxable", "igst", "total"]);
    },
    gstr1Totals() {
      return this.rows.reduce((totals, row) => {
        totals.taxable_0 += Number(row.taxable_0 || 0);
        totals.taxable_5 += Number(row.taxable_5 || 0);
        totals.taxable_12 += Number(row.taxable_12 || 0);
        totals.taxable_18 += Number(row.taxable_18 || 0);
        totals.taxable_28 += Number(row.taxable_28 || 0);
        totals.sgst += Number(row.outward_sgst || 0);
        totals.cgst += Number(row.outward_cgst || 0);
        totals.igst += Number(row.outward_igst || 0);
        totals.total += Number(row.total || 0);
        return totals;
      }, {
        taxable_0: 0,
        taxable_5: 0,
        taxable_12: 0,
        taxable_18: 0,
        taxable_28: 0,
        sgst: 0,
        cgst: 0,
        igst: 0,
        total: 0,
      });
    },
    displayRows() {
      if (!this.isItemwiseReport) return this.rows;

      const groups = new Map();
      this.rows.filter(this.isItemwiseDataRow).forEach((row) => {
        const taxpercent = Number(row.taxpercent || 0);
        const taxtype = row.taxtype || "GST";
        const key = `${taxtype}|${taxpercent}`;
        if (!groups.has(key)) {
          groups.set(key, { taxtype, taxpercent, rows: [] });
        }
        groups.get(key).rows.push(row);
      });

      const displayRows = [];
      Array.from(groups.values())
        .sort((a, b) => a.taxpercent - b.taxpercent || a.taxtype.localeCompare(b.taxtype))
        .forEach((group) => {
          displayRows.push({
            rowkey: `header-${group.taxtype}-${group.taxpercent}`,
            rowtype: "gst_header",
            taxtype: group.taxtype,
            taxpercent: group.taxpercent,
          });

          let quantity = 0;
          let salesValue = 0;
          let gstValue = 0;
          let total = 0;

          group.rows.forEach((row) => {
            displayRows.push(row);
            quantity += Number(row.quantity || 0);
            salesValue += Number(row.sales_value || 0);
            gstValue += Number(row.gst_value || 0);
            total += Number(row.total || 0);
          });

          displayRows.push({
            rowkey: `total-${group.taxtype}-${group.taxpercent}`,
            rowtype: "gst_total",
            taxtype: group.taxtype,
            taxpercent: group.taxpercent,
            quantity,
            sales_value: salesValue,
            gst_value: gstValue,
            total,
          });
        });

      return displayRows;
    },
    columns() {
      if (this.filters.report_type === "itemwise") {
        return itemwiseColumns;
      }
      if (this.filters.report_type === "gstr1") {
        return gstr1Columns;
      }
      if (this.filters.report_type === "hsn") {
        return hsnColumns;
      }
      if (this.filters.report_type === "system_internal") {
        return systemInternalColumns;
      }
      return taxColumns;
    },
  },
  created() {
    if (this.isSystemInternalUser) {
      this.filters.report_type = "system_internal";
    }
    this.fetchReport();
  },
  methods: {
    today() {
      const date = new Date();
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    },
    defaultStartDate() {
      const date = new Date();
      date.setDate(1);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    },
    async fetchReport() {
      this.loading = true;
      try {
        const response = await axios.get("/v1/gstreport", {
          params: {
            startdate: this.filters.startdate,
            enddate: this.filters.enddate,
            report_type: this.filters.report_type,
          },
        });
        this.rows = response.data.rows || [];
        this.summary = response.data.summary || emptySummary();
      } catch (error) {
        this.rows = [];
        this.summary = emptySummary();
        Swal.fire({
          icon: "error",
          title: "Failed to load GST report",
          text: error.response?.data?.detail || error.message,
        });
      } finally {
        this.loading = false;
      }
    },
    applyPeriod({ from, to }) {
      this.filters.startdate = from;
      this.filters.enddate = to;
      this.fetchReport();
    },
    resetFilters() {
      this.filters = {
        startdate: this.defaultStartDate(),
        enddate: this.today(),
        report_type: this.isSystemInternalUser ? "system_internal" : "summary",
      };
      this.fetchReport();
    },
    async downloadReport(exportFormat) {
      this.exportLoading = true;
      this.exportFormat = exportFormat;
      try {
        const response = await axios.get("/v1/gstreport/export", {
          params: {
            startdate: this.filters.startdate,
            enddate: this.filters.enddate,
            report_type: this.filters.report_type,
            export_format: exportFormat,
          },
          responseType: "blob",
        });
        const blob = new Blob([response.data], { type: response.headers["content-type"] });
        const contentDisposition = response.headers["content-disposition"] || "";
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
        const extension = exportFormat === "pdf" ? "pdf" : "xlsx";
        const filename = filenameMatch?.[1] || `gst_report_${this.filters.report_type}_${this.filters.startdate}_${this.filters.enddate}.${extension}`;
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Download failed",
          text: error.response?.data?.detail || error.message,
        });
      } finally {
        this.exportLoading = false;
        this.exportFormat = "";
      }
    },
    formatCurrency(value) {
      return this.formatNumber(value);
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === "") return "";
      return Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatRate(value) {
      return `${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}%`;
    },
    formatPlainRate(value) {
      return Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
    },
    formatHsnQuantity(record) {
      const quantity = this.formatNumber(record.quantity);
      return record.unit ? `${quantity} ${record.unit}` : quantity;
    },
    roundSummaryRow(row) {
      return Object.keys(row).reduce((rounded, key) => {
        rounded[key] = Math.round(Number(row[key] || 0) * 100) / 100;
        return rounded;
      }, {});
    },
    totalGstr1SummaryRows(rows, fields) {
      const totals = {};
      fields.forEach((field) => {
        totals[field] = rows.reduce((sum, row) => sum + Number(row[field] || 0), 0);
      });
      return this.roundSummaryRow(totals);
    },
    formatDate(value) {
      if (!value) return "-";
      const [year, month, day] = String(value).slice(0, 10).split("-");
      return `${day}/${month}/${year}`;
    },
    rowKey(record) {
      if (record.rowkey) return record.rowkey;
      return [
        record.reportdate || "",
        record.itemid || "",
        record.rowtype || "",
        record.serialno || "",
        record.taxtype,
        record.taxpercent,
      ].join("-");
    },
    rowClassName(record) {
      if (record.rowtype === "gst_header") return "gst-section-row";
      if (record.rowtype === "gst_total") return "gst-total-row";
      return "";
    },
    isItemwiseDataRow(row) {
      return Boolean(
        row &&
        row.rowtype === "data" &&
        (row.itemid || row.itemcode || row.itemname) &&
        (
          row.quantity != null ||
          row.sales_value != null ||
          row.gst_value != null ||
          row.total != null
        )
      );
    },
  },
};
</script>

<style scoped src="./report-theme.css"></style>
<style scoped>
.report-actions {
  min-height: 42px;
}
.report-action-btn {
  min-width: 112px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  border-radius: 8px;
}
.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(210px, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}
.summary-card {
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
}
.summary-card span,
.summary-card small {
  display: block;
  color: #64748b;
}
.summary-card strong {
  display: block;
  margin: 2px 0;
  font-size: 24px;
}
.summary-card small {
  font-size: 12px;
}
.summary-card.tone-negative {
  border-left-color: #ef4444;
}
.summary-card.tone-negative strong {
  color: #b91c1c;
}
.summary-card.tone-positive {
  border-left-color: #10b981;
}
.summary-card.tone-positive strong {
  color: #047857;
}
.breakdown-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(250px, 1fr));
  gap: 14px;
  margin-bottom: 20px;
}
.breakdown-card {
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}
.breakdown-card h6 {
  margin: 0 0 10px;
}
.breakdown-card div {
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
  font-variant-numeric: tabular-nums;
}
.table-title {
  margin-bottom: 10px;
  color: #0f172a;
}
.itemwise-report-wrap {
  background: #fff;
}
.gst-itemwise-table {
  width: 100%;
  min-width: 1040px;
  border-collapse: collapse;
  color: #0f172a;
}
.gst-itemwise-table th,
.gst-itemwise-table td {
  padding: 6px 8px;
  vertical-align: middle;
  white-space: nowrap;
}
.gst-itemwise-table thead th {
  background: #1e293b;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
}
.gst-itemwise-table .numeric {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.gst-itemwise-table .empty-row {
  padding: 18px;
  text-align: center;
  color: #64748b;
}
.gst-itemwise-table .slab-title {
  padding-top: 10px;
  text-align: center;
  font-weight: 800;
}
.gst-itemwise-table .total-label {
  text-align: right;
  font-weight: 800;
}
:deep(.gst-section-row td) {
  background: #f8fafc;
  font-weight: 800;
  color: #0f172a;
}
:deep(.gst-total-row td) {
  background: #fff7ed;
  font-weight: 800;
  border-top: 1px dashed #f97316;
}
.gst-itemwise-table .gst-total-row td {
  border-top: 1px dashed #475569;
  font-weight: 800;
}
.gstr1-end-summary {
  margin-top: 18px;
  color: #0f172a;
}
.gstr1-total-strip {
  display: grid;
  grid-template-columns: minmax(180px, 2fr) repeat(9, minmax(92px, 1fr));
  min-width: 1120px;
  border: 1px solid #475569;
  border-bottom: none;
  background: #f8fafc;
  overflow-x: auto;
}
.gstr1-total-strip span,
.gstr1-total-strip strong {
  padding: 6px 8px;
  border-right: 1px solid #475569;
  text-align: right;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.gstr1-total-strip span {
  text-align: center;
  font-weight: 800;
}
.gstr1-total-strip strong:last-child {
  border-right: none;
}
.gstr1-tax-summary-grid {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  overflow-x: auto;
  border: 1px solid #475569;
  padding: 14px;
  background: #fff;
}
.gstr1-tax-table {
  min-width: 315px;
  border-collapse: collapse;
  font-size: 12px;
  background: #fff;
}
.gstr1-tax-table th,
.gstr1-tax-table td {
  border: 1px solid #475569;
  padding: 5px 7px;
  white-space: nowrap;
}
.gstr1-tax-table th {
  background: #f8fafc;
  font-weight: 800;
  text-align: center;
}
.gstr1-tax-table .numeric {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.gstr1-tax-table .summary-total-row td {
  font-weight: 800;
  background: #f8fafc;
}
@media (max-width: 992px) {
  .report-header-bar {
    flex-direction: column;
  }
  .export-actions {
    width: 100%;
    justify-content: flex-start;
  }
  .summary-grid,
  .breakdown-grid {
    grid-template-columns: 1fr;
  }
  .gstr1-tax-summary-grid {
    flex-direction: column;
  }
}
</style>
